import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import type { OrderProductionListItem } from "@/lib/types";

interface Props {
  item: OrderProductionListItem;
  target: "PRONTO" | "PARCIAL" | "SEM_MERCADORIA";
  onCancel: () => void;
  onSubmit: (input: { useStock?: boolean; quantity?: number; reason?: string }) => Promise<void>;
}

export function ProductionDecisionModal({ item, target, onCancel, onSubmit }: Props) {
  const [quantity, setQuantity] = useState(String(item.producedQuantity ?? item.productionQuantity));
  const [reason, setReason] = useState(item.reason ?? "");
  const [useStock, setUseStock] = useState(!!item.useStock);
  const [submitting, setSubmitting] = useState(false);

  const reasonRequired = target === "PARCIAL" || target === "SEM_MERCADORIA";

  const submit = async () => {
    const trimmedReason = reason.trim();
    if (reasonRequired && !trimmedReason) {
      toast.error("Motivo obrigatório");
      return;
    }
    const q = Number(quantity);
    if (target === "PARCIAL" && (!Number.isFinite(q) || q <= 0)) {
      toast.error("Quantidade inválida");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        useStock: target === "PRONTO" ? useStock : undefined,
        quantity: target === "PARCIAL" ? q : undefined,
        reason: trimmedReason || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    target === "PRONTO" ? "Marcar como pronto"
    : target === "PARCIAL" ? "Informar quantidade produzida"
    : "Em falta";

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {item.productName} · base <strong>{item.productionQuantity}</strong> {item.unit}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {target === "PRONTO" && (
            <label className="flex items-center gap-2 rounded-md border border-border p-3 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={useStock}
                onChange={(event) => setUseStock(event.target.checked)}
              />
              <span>
                <span className="font-medium">Veio do estoque</span>
                <span className="ml-1 text-xs text-muted-foreground">(não consome produção, deduz do estoque)</span>
              </span>
            </label>
          )}
          {target === "PARCIAL" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Quantidade produzida ({item.unit})
              </label>
              <Input
                type="number"
                min="0"
                step="0.001"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                autoFocus
              />
            </div>
          )}
          {(target === "PARCIAL" || target === "SEM_MERCADORIA") && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Motivo <span className="text-destructive">*</span>
              </label>
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={target === "PARCIAL" ? "Por que não foi produzido o total?" : "Por que está em falta?"}
                className="min-h-[80px]"
                autoFocus={target === "SEM_MERCADORIA"}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="hero" size="sm" onClick={() => void submit()} disabled={submitting}>
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
